import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';
import managerAPI from './managerApi';

axiosRetry(axios, {
  retries: 1,
  retryDelay: (retryCount) => retryCount * 1000,
  retryCondition: (error) => {
    if (axios.isCancel(error)) {
      return false;
    }
    return true;
  },
  onRetry: async (retryCount, error, requestConfig) => {},
});

/**
 * Performs a cancellable GET request for report data such as ECG or beat files. Issues the axios call with an AbortController signal, registers that controller in the API manager under the cancel key so a tab switch can abort it, and deregisters it once the request settles or throws.
 *
 * @param url - Absolute URL to fetch.
 * @param options - Extra axios request configuration merged with the abort signal.
 * @param config - Cancellation settings, whose cancelKey groups the request in the API manager; a null key uses the global list.
 * @returns A promise resolving to the axios response carrying the typed payload.
 */
const axiosWrapper = async <TResponse>(
  url: string,
  options?: AxiosRequestConfig,
  config: { cancelKey?: string | null } = { cancelKey: null },
): Promise<AxiosResponse<TResponse>> => {
  const { cancelKey = null } = config;
  const abortController = new AbortController();
  try {
    const promise = axios.get<TResponse>(url, { ...options, signal: abortController.signal });

    // Save to store
    managerAPI.pushStoreCancelAPI(cancelKey, abortController);

    const result = await promise;

    // After API done, remove from store
    managerAPI.removeStoreCancelAPI(cancelKey, abortController);

    return result;
  } catch (error) {
    managerAPI.removeStoreCancelAPI(cancelKey, abortController);
    throw error;
  }
};
export default axiosWrapper;
